import {
  AppRoot,
  AppStore,
  CMicroAppGuest,
  configureStore,
  getAppConfigValue,
  initServiceWorker,
  listenStorageEvents,
  OnInitCallback,
  OnMountCallback,
  OnUnMountCallback,
  RootState,
  setAppConfig,
} from '@elliemae/pui-app-sdk';
import { Logger } from '@elliemae/pui-diagnostics';
import { startRefreshTokenProcess } from '@frontend/auth';
import { Dialog } from '@frontend/elliemae-helpers';
import { initializeLogRocket } from '@frontend/logrocket';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import { useState } from 'react';
import { createRoot, Root } from 'react-dom/client';
import 'reflect-metadata';
import {
  getPPEServicesRefreshToken,
  setPPEServicesTokens,
} from './api/eppsService';
import appConfig from './app.config.json';
import SessionWatcher from './Auth/sessionWatcher';
import './index.css';
import { AppRoutes } from './route';
import { logger } from './utils/logger';
import { getBasePath, getHomeRoute, setHomeRoute } from './utils/paths';

listenStorageEvents();
setAppConfig(appConfig);
initializeLogRocket();

// if (process.env.NODE_ENV === 'development') {
//   // eslint-disable-next-line global-require, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-var-requires
//   const axe = require('@axe-core/react');
//   // eslint-disable-next-line @typescript-eslint/no-unsafe-call
//   axe(React, ReactDOM, 1000);
// }

// eslint-disable-next-line import/no-mutable-exports
export let app: CMicroAppGuest | null = null;
let store: AppStore | null = null;
let root: Root | null = null;

export const Application = () => {
  if (!app || !store) throw new Error('Application is not initialized');
  const appProps = app.getProps();
  const [isOpenOffset, setIsOpenOffset] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const existingRefreshToken = getPPEServicesRefreshToken();
  if (existingRefreshToken) {
    startRefreshTokenProcess(existingRefreshToken, {
      newToken: (newToken: string, newRefreshToken: string) =>
        setPPEServicesTokens(newToken, newRefreshToken),
      hardLimitOffset: () => {
        setIsOpenOffset(true);
      },
      hardLimit: () => {
        window.close(); // this works only sometimes

        // We can't always close the tab, depending on how the tab was opened
        setIsOpen(true);
      },
    });
  }

  return (
    <QueryClientProvider client={queryClient}>
      <AppRoot
        store={store}
        history={appProps.history}
        basename={getHomeRoute()}
        theme={appProps.theme}
        manageSession
      >
        <SessionWatcher />
        <AppRoutes />

        <Dialog
          isOpen={isOpenOffset}
          hideCancel
          onApply={() => setIsOpenOffset(false)}
          onClickOutside={() => setIsOpenOffset(false)}
          title="Log out required soon"
          width="500px"
          bodyHeight="220px"
          applyText="Okay"
          centered
        >
          <div>
            You will be logged out soon. Please save your work and exit.
          </div>
        </Dialog>
        <Dialog
          isOpen={isOpen}
          hideApply
          hideCancel
          title="Log out required"
          width="500px"
          bodyHeight="220px"
          centered
        >
          <div>Close this tab to complete log out.</div>
        </Dialog>
      </AppRoot>
    </QueryClientProvider>
  );
};

export const onInit: OnInitCallback = ({ history, homeRoute }) => {
  (logger as Logger).setOptions?.({
    environment: getAppConfigValue('laneTag'),
    appVersion: window.emui.version,
    instanceId: sessionStorage.getItem('instanceId') || '',
    userId: sessionStorage.getItem('userId') || '',
  });
  logger.info('Application initialized');
  setHomeRoute(homeRoute);
  store = configureStore({} as RootState, history);
};

const onMount: OnMountCallback = ({ containerId }) => {
  const appNode = document.getElementById(containerId);
  root = createRoot(appNode!);
  root.render(<Application />);
};

const onUnmount: OnUnMountCallback = () => {
  if (root) {
    root.unmount();
    root = null;
  }
  return null;
};

app = CMicroAppGuest.getInstance({ logger, onInit, onMount, onUnmount });

initServiceWorker(getBasePath());
